/**
 * PublicLinkPanel — публичная ссылка на этот отчёт.
 *
 * Отчёт показывают тем, у кого нет доступа в кабинет: агентству, инвестору,
 * коллеге. Пересылать логин ради одного документа неприлично, поэтому у
 * отчёта есть собственный адрес и собственный пароль.
 *
 * Пароль генерирует система, а не человек: его придётся диктовать по
 * телефону, и «qwerty123» здесь опаснее, чем где бы то ни было — за ссылкой
 * стоит вся экономика проекта.
 *
 * Ссылка и пароль копируются РАЗДЕЛЬНО, двумя кнопками. Одна кнопка
 * «скопировать всё» удобнее ровно до того момента, когда адрес и пароль
 * уезжают в один и тот же чат: защита тогда перестаёт существовать.
 *
 * Панель видит только тот, кто открыл отчёт в системе. Гостю её не
 * показывают — страница просто не получает этих пропов.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

import {Button} from 'ui/components/atoms/Button';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusM} from 'ui/tokens/ts/radii';

import {MEASURE, TABULAR} from '../helpers';
import type {PublicLink} from '../types';
import {CopyButton} from './CopyButton';
import {ReportCard} from './ReportCard';

export interface PublicLinkPanelProps {
  /** Действующая ссылка. `null` — её ещё не создавали или её отозвали. */
  link?: PublicLink | null;
  onCreate?: () => void;
  onRevoke?: () => void;
  /** Запрос в полёте: кнопки не должны срабатывать дважды. */
  busy?: boolean;
  /**
   * Сообщение последней неудачной попытки — создать ссылку или отозвать её.
   *
   * `null`/не задано — ошибки не было. Панель не знает, какого именно
   * действия она касается: хост сбрасывает её сам перед новой попыткой.
   */
  error?: string | null;
}

const Title = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
});

const ErrorText = styled(Box)(({theme}) => ({
  ...text('body.m'),
  color: theme.palette.bad.main,
}));

const Explain = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  maxWidth: MEASURE,
});

/** Подпись поля — над самим значением, а не рядом: значения длинные. */
const FieldLabel = styled(Box)({
  ...text('label.s'),
  color: surfaceVar('onSurfaceMuted'),
});

/**
 * Значение, которое читают и диктуют.
 *
 * Моноширинные цифры и рамка: и адрес, и пароль здесь не текст, а данные —
 * их сверяют посимвольно. Адрес переносится по любому месту, иначе длинная
 * ссылка распирает карточку.
 *
 * Типограф сюда не заходит (`data-no-typograph` на самом элементе). Он
 * обрамляет дефисы невидимым клеем, чтобы слово не рвалось переносом, — и
 * в прозе это верно, а в пароле «kudr-9m4t» превращается в строку, которая
 * выглядит правильной, но при выделении мышью копируется вместе с клеем и
 * не подходит. Кнопки копирования берут значение из данных и от этого не
 * страдают, но человек копирует и выделением тоже.
 */
const Value = styled(Box)({
  ...text('body.l'),
  ...TABULAR,
  color: surfaceVar('onSurface'),
  padding: spacingPx('s'),
  borderRadius: RadiusM,
  border: `1px solid ${surfaceVar('surface2')}`,
  backgroundColor: surfaceVar('surface1'),
  overflowWrap: 'anywhere',
});

export function PublicLinkPanel({
  link,
  onCreate,
  onRevoke,
  busy = false,
  error,
}: PublicLinkPanelProps) {
  const {t} = useTranslation();

  return (
    <ReportCard padding='l'>
      <Stack gap='m'>
        <Stack gap='xs'>
          <Title>{t('Публичная ссылка', 'Публичная ссылка')}</Title>
          <Explain>
            {link
              ? t(
                  'Отчёт открывается по этому адресу без входа в систему — после ввода пароля. Пароль отправляйте отдельно от ссылки.',
                  'Отчёт открывается по этому адресу без входа в систему — после ввода пароля. Пароль отправляйте отдельно от ссылки.',
                )
              : t(
                  'Ссылка открывает этот отчёт тем, у кого нет доступа в систему. Пароль к ней придумывать не нужно, мы сгенерируем его сами.',
                  'Ссылка открывает этот отчёт тем, у кого нет доступа в систему. Пароль к ней придумывать не нужно, мы сгенерируем его сами.',
                )}
          </Explain>
        </Stack>

        {link ? (
          <Stack gap='m'>
            <Stack gap='xs'>
              <FieldLabel>{t('Адрес', 'Адрес')}</FieldLabel>
              <Value data-no-typograph>{link.url}</Value>
            </Stack>

            {link.password ? (
              <Stack gap='xs'>
                <FieldLabel>{t('Пароль', 'Пароль')}</FieldLabel>
                <Value data-no-typograph>{link.password}</Value>
              </Stack>
            ) : (
              <Stack gap='xs'>
                <FieldLabel>{t('Пароль', 'Пароль')}</FieldLabel>
                {/* Пустая рамка здесь хуже отсутствующего поля: человек решит,
                    что пароль не создался, и будет ждать. Называем выход. */}
                <Explain>
                  {t(
                    'Пароль к этой ссылке больше не читается. Чтобы выдать доступ заново, удалите ссылку и создайте заново — у новой будет другой адрес и другой пароль, а прежние получатели доступ потеряют.',
                    'Пароль к этой ссылке больше не читается. Чтобы выдать доступ заново, удалите ссылку и создайте заново — у новой будет другой адрес и другой пароль, а прежние получатели доступ потеряют.',
                  )}
                </Explain>
              </Stack>
            )}

            <Row gap='s' wrap='wrap'>
              <CopyButton
                size='m'
                variant='outline'
                color='primary'
                value={link.url}
                label={t('Скопировать адрес', 'Скопировать адрес')}
              />
              {link.password && (
                <CopyButton
                  size='m'
                  variant='outline'
                  color='primary'
                  value={link.password}
                  label={t('Скопировать пароль', 'Скопировать пароль')}
                />
              )}
              {/* Удаление — единственное необратимое действие панели, и
                  красным оно названо честно: адрес после него не вернуть,
                  а разосланная ссылка перестанет открываться. */}
              <Button size='m' variant='ghost' color='bad' onClick={onRevoke} disabled={busy}>
                {t('Удалить ссылку', 'Удалить ссылку')}
              </Button>
            </Row>
          </Stack>
        ) : (
          <Row gap='s' wrap='wrap'>
            <Button size='m' variant='outline' color='primary' onClick={onCreate} disabled={busy}>
              {t('Создать публичную ссылку', 'Создать публичную ссылку')}
            </Button>
          </Row>
        )}

        {/* `role='status'` объявляет ошибку скринридеру: она появляется
            после действия (создание/удаление ссылки), а не рядом с полем,
            на которое можно было бы повесить `aria-describedby`. */}
        {error && <ErrorText role='status'>{error}</ErrorText>}
      </Stack>
    </ReportCard>
  );
}
